import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { RETURN_ADDRESS, CONTACT_INFO, siteImages } from '../data/mock';
import { MapPin, Clock, CheckCircle2, XCircle, Package, ArrowRight, Cpu, Mail, Phone } from 'lucide-react';

const ReturnsPage = () => {
  useEffect(() => {
    document.title = 'Returns & Refund Policy | Bright Tech Supply';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="">
        {/* Banner Section */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={siteImages.returnsBanner}
              alt="Returns Policy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
          </div>
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-50 border border-athletic-100 rounded-full text-athletic-700 text-xs font-semibold mb-4">
                <Cpu className="w-3.5 h-3.5" />
                HASSLE-FREE RETURNS
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-gray-900 font-extrabold mb-3">
                Returns & Refunds
              </h1>
              <p className="text-gray-600 max-w-lg">
                We want you to be completely satisfied with your purchase
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Return Address Card - Prominent */}
          <div className="bg-gradient-to-br from-athletic-50 to-power-50 border border-athletic-100 rounded-2xl p-8 mb-12">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-athletic-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-7 h-7 text-athletic-700" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-gray-900 mb-2">Return Address</h2>
                <p className="text-athletic-700 font-semibold text-lg leading-relaxed">
                  {RETURN_ADDRESS.street}<br />
                  {RETURN_ADDRESS.city}, {RETURN_ADDRESS.state} {RETURN_ADDRESS.zip}<br />
                  {RETURN_ADDRESS.country}
                </p>
              </div>
            </div>
          </div>

          {/* Policy Overview Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-athletic-700" />
              </div>
              <h3 className="font-display font-bold text-gray-900 text-lg mb-2">30-Day Window</h3>
              <p className="text-gray-500 text-sm">Return within 30 days of delivery for a full refund</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Package className="w-7 h-7 text-athletic-700" />
              </div>
              <h3 className="font-display font-bold text-gray-900 text-lg mb-2">Original Condition</h3>
              <p className="text-gray-500 text-sm">Items must be unused and in original packaging</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-athletic-700" />
              </div>
              <h3 className="font-display font-bold text-gray-900 text-lg mb-2">Full Refund</h3>
              <p className="text-gray-500 text-sm">Refunds processed within 5-7 business days</p>
            </div>
          </div>

          {/* Eligible / Not Eligible */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
              <h3 className="font-display font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-athletic-700" />
                Eligible for Returns
              </h3>
              <ul className="space-y-3">
                {[
                  'Unused items in original packaging',
                  'Items returned within 30 days',
                  'Defective or damaged products',
                  'Wrong items received',
                  'Items with all tags attached'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-athletic-700 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
              <h3 className="font-display font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-power-500" />
                Not Eligible for Returns
              </h3>
              <ul className="space-y-3">
                {[
                  'Used or worn items',
                  'Items without original packaging',
                  'Items returned after 30 days',
                  'Items damaged by misuse',
                  'Personal hygiene items once opened'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600 text-sm">
                    <XCircle className="w-4 h-4 text-power-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* How to Return */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 mb-12">
            <h3 className="font-display font-bold text-gray-900 text-xl mb-6">How to Return an Item</h3>
            <div className="space-y-6">
              {[
                { num: '1', title: 'Contact Us', desc: 'Email us or call to request a return authorization' },
                { num: '2', title: 'Pack Your Item', desc: 'Securely pack the item in its original packaging' },
                { num: '3', title: 'Ship It Back', desc: 'Send the package to our return address above' },
                { num: '4', title: 'Get Your Refund', desc: 'Refund processed within 5-7 business days of receipt' }
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-athletic-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{step.num}</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">{step.title}</h4>
                    <p className="text-gray-500 text-sm">{step.desc}</p>
                  </div>
                  {i < 3 && <ArrowRight className="w-5 h-5 text-gray-400 hidden md:block" />}
                </div>
              ))}
            </div>
          </div>

          {/* Contact for Returns */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8">
            <h3 className="font-display font-bold text-gray-900 text-xl mb-4">Need Help with a Return?</h3>
            <p className="text-gray-500 mb-6">Our customer support team is here to assist you with any questions about returns or refunds.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-athletic-500 text-white font-semibold rounded-xl hover:bg-athletic-400 transition-colors"
              >
                <Mail className="w-5 h-5" />
                {CONTACT_INFO.email}
              </a>
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-900 font-semibold rounded-xl hover:bg-gray-200 transition-colors border border-gray-200"
              >
                <Phone className="w-5 h-5" />
                {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ReturnsPage;
